import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Download, Clock, ExternalLink, ChevronDown, ChevronUp, ArrowLeft, FileText } from 'lucide-react';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';

const BillingHistoryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  useEffect(() => {
    const fetchBillingHistory = async () => {
      try {
        setLoading(true);
        const user = getCurrentUser();
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        // Get billing history from Stripe invoices
        const response = await api.get('/stripe/invoices');
        setBillingHistory(response.data);
      } catch (err) {
        setError('Failed to load billing history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const toggleInvoiceDetails = (invoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
    } else {
      setExpandedInvoice(invoiceId);
    }
  };


  return (
    <div className="flex flex-col flex-1 bg-gray-100 dark:bg-gray-900 color-transition">
      <div className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden color-transition">
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center">
                <Link
                  to="/account/subscription"
                  className="inline-flex items-center mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Link>
                <h1 className="text-lg font-medium text-gray-900 dark:text-white">Billing History</h1>
              </div>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 dark:border-blue-400"></div>
              </div>
            ) : error ? (
              <div className="p-6 text-center">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                  <p>{error}</p>
                </div>
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="p-6 text-center">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/20 p-3 w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No billing history</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  You don't have any billing history yet. This is where your invoices and payments will appear once you subscribe to a paid plan.
                </p>
                <Link 
                  to="/account/subscription" 
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  View Plans
                </Link>
              </div>
            ) : (
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-200 dark:bg-gray-700">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Description
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {billingHistory.map((invoice) => (
                        <React.Fragment key={invoice.invoiceId}>
                          <tr
                            className="hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-150"
                            onClick={() => toggleInvoiceDetails(invoice.invoiceId)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                                {formatDate(invoice.invoiceDate)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {invoice.invoiceNumber || invoice.description || 'Subscription payment'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {formatCurrency(invoice.total, invoice.currency)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                invoice.status === 'paid'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                  : invoice.status === 'open'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                              }`}>
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 ml-3 transition"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleInvoiceDetails(invoice.invoiceId);
                                }}
                              >
                                {expandedInvoice === invoice.invoiceId ? (
                                  <ChevronUp className="h-5 w-5" />
                                ) : (
                                  <ChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded invoice details */}
                          {expandedInvoice === invoice.invoiceId && (
                            <tr className="bg-gray-100 dark:bg-gray-700">
                              <td colSpan="5" className="px-6 py-4">
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Invoice Number</dt>
                                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{invoice.invoiceNumber || 'N/A'}</dd>
                                    </div>
                                    <div>
                                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Amount Paid</dt>
                                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrency(invoice.amountPaid, invoice.currency)}</dd>
                                    </div>
                                    {invoice.lineItems && invoice.lineItems.length > 0 && (
                                      <div className="col-span-2">
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Items</dt>
                                        <dd className="mt-1">
                                          <ul className="border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-200 dark:divide-gray-700">
                                            {invoice.lineItems.map((item, index) => (
                                              <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                                                <div className="w-0 flex-1 flex items-center">
                                                  <span className="flex-1 w-0 truncate text-gray-900 dark:text-white">
                                                    {item.description} {item.quantity > 1 ? `(${item.quantity}x)` : ''}
                                                  </span>
                                                </div>
                                                <div className="ml-4 flex-shrink-0 text-gray-900 dark:text-white">
                                                  {formatCurrency(item.amount, invoice.currency)}
                                                </div>
                                              </li>
                                            ))}
                                          </ul>
                                        </dd>
                                      </div>
                                    )}
                                  </dl>
                                  <div className="mt-6 flex justify-end space-x-3">
                                    {invoice.invoicePdfUrl && (
                                      <a
                                        href={invoice.invoicePdfUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                      >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download Invoice
                                      </a>
                                    )}
                                    {invoice.hostedInvoiceUrl && (
                                      <a
                                        href={invoice.hostedInvoiceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                      >
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        View Invoice
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistoryPage;